const alpineHelpers = {
  // Store original values for each modal
  originalValues: {},

  // Helper function to open Alpine modals from outside of the Alpine.js component scope
  openModal: function(modalId) {
    const modal = document.querySelector('[x-ref=modal-' + modalId + ']');
    if(modal && Alpine) {
      const data = Alpine.$data(modal);
      if(data && data.openModal) {
        data.openModal();
        // Clear any existing captured values for this modal so we capture fresh state
        delete this.originalValues[modalId];
        // Capture original values after modal opens - try multiple times with increasing delays
        setTimeout(() => this.captureOriginalValues(modalId), 100);
        setTimeout(() => this.captureOriginalValues(modalId), 300);
        setTimeout(() => this.captureOriginalValues(modalId), 500);
      }
    }
  },

  // Helper function to close Alpine modals from outside of the Alpine.js component scope
  closeModal: function(modalId, event) {
    if(event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const modal = document.querySelector('[x-ref=modal-' + modalId + ']');
    if(modal && Alpine) {
      const data = Alpine.$data(modal);
      if(data && data.closeModal) {
        data.closeModal();
      }
    }
  },

  // Cancel modal with revert (shows confirmation dialog first)
  cancelModal: function(modalId, event) {
    // Stop event immediately to prevent multiple triggers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    // Use custom confirmation dialog
    if (window.confirmDialog) {
      window.confirmDialog.show({
        title: 'Discard changes?',
        message: 'Any unsaved changes will be lost.',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        onConfirm: () => {
          // Skip revert for new blocks - they have no original data and will be deleted
          if (!modalId.startsWith('new-')) {
            this.revertChanges(modalId);
          }
          this.closeModal(modalId);
        }
      });
    } else {
      // Fallback to native confirm if dialog not available
      if (confirm('Are you sure you want to close this modal? Any unsaved changes will be lost.')) {
        // Skip revert for new blocks - they have no original data and will be deleted
        if (!modalId.startsWith('new-')) {
          this.revertChanges(modalId);
        }
        this.closeModal(modalId);
      }
    }
  },

  // Capture original form values
  captureOriginalValues: function(modalId) {
    const modal = document.querySelector('[x-ref=modal-' + modalId + ']');
    if(!modal) {
      return;
    }

    // Don't overwrite if we already have values
    if(this.originalValues[modalId] && Object.keys(this.originalValues[modalId]).length > 0) {
      return;
    }

    // Look for inputs in the entire modal, not just in a form
    const inputs = modal.querySelectorAll('input, textarea, select');

    if(inputs.length === 0) {
      return;
    }

    this.originalValues[modalId] = {};
    const processedRadios = new Set(); // Track radio groups we've already processed

    inputs.forEach(input => {
      const key = input.name || input.id;
      if(key) {
        if(input.type === 'file') {
          // For file inputs, we can only store empty string and can only revert to empty
          this.originalValues[modalId][key] = '';
        } else if(input.type === 'radio') {
          // For radio buttons, store the VALUE of the checked radio (not the checked state)
          // Only process each radio group once
          if(!processedRadios.has(key)) {
            processedRadios.add(key);
            const checkedRadio = modal.querySelector(`input[name="${CSS.escape(key)}"]:checked`);
            this.originalValues[modalId][key] = checkedRadio ? checkedRadio.value : null;
          }
        } else if(input.type === 'checkbox') {
          this.originalValues[modalId][key] = input.checked;
        } else {
          this.originalValues[modalId][key] = input.value;
        }
      }
    });
  },

  // Revert form changes
  revertChanges: function(modalId) {
    const modal = document.querySelector('[x-ref=modal-' + modalId + ']');
    if(!modal) {
      return;
    }
    if(!this.originalValues[modalId]) {
      return;
    }

    Object.keys(this.originalValues[modalId]).forEach(key => {
      const originalValue = this.originalValues[modalId][key];

      // Look for inputs in the entire modal, not just in a form
      const input = modal.querySelector(`[name="${CSS.escape(key)}"], [id="${CSS.escape(key)}"]`);

      if(input) {
        if(input.type === 'file') {
          // File inputs can only be set to empty string
          input.value = '';
        } else if(input.type === 'radio') {
          // For radio buttons, find and check the one with the matching value
          const radios = modal.querySelectorAll(`input[name="${CSS.escape(key)}"]`);
          radios.forEach(radio => {
            if(radio.value === originalValue) {
              radio.checked = true;
              // Trigger events to update Alpine.js bindings
              radio.dispatchEvent(new Event('input', { bubbles: true }));
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
              radio.checked = false;
            }
          });
        } else if(input.type === 'checkbox') {
          input.checked = originalValue;
          // Trigger events to update Alpine.js bindings
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          input.value = originalValue;
          // Trigger events to update Alpine.js bindings
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));

          // Check if this input is associated with a Trix editor
          const trixEditor = modal.querySelector(`trix-editor[input="${CSS.escape(input.id)}"]`);
          if(trixEditor && trixEditor.editor) {
            // Update the Trix editor's visual content to match the reverted value
            trixEditor.editor.loadHTML(originalValue || '');
          }
        }
      }
    });
  },

  // Refresh conditional fields in a container (or entire document)
  refreshConditionalFields: function(container = document) {
    // Try to find form via closest first, then fallback to document search
    let form = container.closest ? container.closest('form.prjs-form') : null;
    if (!form) {
      form = document.querySelector('form.prjs-form');
    }

    if (!form) {
      return;
    }

    // Find conditional fields in the container
    const conditionalFields = container.querySelectorAll('[data-condition-field]');

    if (conditionalFields.length === 0) return;

    // Determine search scope based on container
    const isModalContainer = container !== document && container.classList?.contains('modal');

    // Use the same evaluation logic as render-field.twig
    const evaluate = (fieldName) => {
      // For modals, only evaluate fields in the container; for document, evaluate all fields in form
      const fields = isModalContainer
        ? container.querySelectorAll('[data-condition-field][data-condition-value]')
        : form.querySelectorAll('[data-condition-field][data-condition-value]');

      fields.forEach((wrapper) => {
        const targetName = wrapper.getAttribute('data-condition-field');
        const fieldId = wrapper.getAttribute('data-field-id');
        if (fieldName && fieldName !== targetName) return;
        const expected = wrapper.getAttribute('data-condition-value');

        // For modals, search in container first; for document, search in form
        let el = isModalContainer
          ? container.querySelector(`[name="${CSS.escape(targetName)}"]`)
          : form.querySelector(`[name="${CSS.escape(targetName)}"]`);

        // If not found and we're in a modal, fall back to form search
        if (!el && isModalContainer) {
          el = form.querySelector(`[name="${CSS.escape(targetName)}"]`);
        }

        if (!el) {
          // Try radio buttons with same scope logic
          const radios = isModalContainer
            ? container.querySelectorAll(`[name="${CSS.escape(targetName)}"]`)
            : form.querySelectorAll(`[name="${CSS.escape(targetName)}"]`);

          if (radios.length) {
            el = Array.from(radios).find(r => r.checked) || radios[0];
          } else if (isModalContainer) {
            // Fall back to form-wide search for modals
            const formRadios = form.querySelectorAll(`[name="${CSS.escape(targetName)}"]`);
            if (formRadios.length) {
              el = Array.from(formRadios).find(r => r.checked) || formRadios[0];
            }
          }
        }

        let currentValue = null;
        if (el) {
          if (el.type === 'checkbox') {
            currentValue = el.checked ? '1' : '0';
          } else if (el.type === 'radio') {
            // Search for checked radio with same scope logic
            let checked = isModalContainer
              ? container.querySelector(`[name="${CSS.escape(targetName)}"]:checked`)
              : form.querySelector(`[name="${CSS.escape(targetName)}"]:checked`);

            // Fall back to form search for modals
            if (!checked && isModalContainer) {
              checked = form.querySelector(`[name="${CSS.escape(targetName)}"]:checked`);
            }
            currentValue = checked ? checked.value : null;
          } else {
            currentValue = el.value;
          }
        }

        if (currentValue == expected) {
          wrapper.style.display = '';
          // Enable all inputs within the field
          wrapper.querySelectorAll('input, select, textarea').forEach(input => {
            input.disabled = false;
          });
        } else {
          wrapper.style.display = 'none';
          // Disable all inputs within the field
          wrapper.querySelectorAll('input, select, textarea').forEach(input => {
            input.disabled = true;
            // Clear custom validity when disabling to prevent validation errors on hidden fields
            if (input.setCustomValidity) {
              input.setCustomValidity('');
            }
          });
        }
      });
    };

    // Run evaluation
    evaluate();
  }
};

export default alpineHelpers;
